import { type KeyboardEvent, type ReactNode, useCallback } from 'react';
import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { GripVertical } from 'lucide-react';

import {
    dropMove,
    type SortableHandle,
    type SortableLayout,
    stepMove,
    strategyFor,
} from '@/components/Sortable/sortableMoves';
import { cn } from '@/util/utils';

/*
  The prototype's reorder (scripts/screens/lookbook-new.js, grip() and reorder(), 11 Sep 2026): a
  six-dot grip is the handle; press it and its row or tile lifts, drop it on another and the list
  takes the new order. The same grip is a keyboard control: focused, the arrow keys move its entry
  one place. One implementation for every list that reorders (the lookbook pages, ALS-81; the
  product tiles and the photo and video books can move onto it).

  The prototype drags with native HTML5 drag, which a phone never starts. This is dnd-kit, an
  adapter over it to `onMove(from, to)`: a mouse lifts after 4px, a finger after holding 150ms so
  the page still scrolls under a swipe. The order is controlled, so the list belongs to the caller.
  dnd-kit's own keyboard sensor (space to pick up, arrows, space to drop) is not used: the grip
  answers the arrows itself, one move per press, as the prototype does.
*/

export interface SortableProps {
    /** Every entry's stable id, in order. */
    ids: readonly string[];
    onMove: (from: number, to: number) => void;
    /**
     * How the entries sit: a list (rows, the default) or a grid of tiles that wrap, which needs
     * dnd-kit to measure both axes (the media books' photo grid, ALS-79).
     */
    layout?: SortableLayout;
    children: ReactNode;
}

export function Sortable({ ids, onMove, layout = 'list', children }: SortableProps) {
    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    );
    const onDragEnd = useCallback(
        (event: DragEndEvent) => {
            const move = dropMove(
                ids,
                String(event.active.id),
                event.over ? String(event.over.id) : null,
            );
            if (move) onMove(move.from, move.to);
        },
        [ids, onMove],
    );
    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
            // dnd-kit puts focus back on the grip a frame after a drop. Nothing here needs it (the
            // grip's own arrow keys keep their focus), and when a drawer or dialog opens in that
            // frame, the focus leaving it closes it again.
            accessibility={{ restoreFocus: false }}
        >
            <SortableContext items={[...ids]} strategy={strategyFor(layout)}>
                {children}
            </SortableContext>
        </DndContext>
    );
}

export interface SortableGripProps {
    /** "Reorder {name}. Arrow keys move it." */
    label: string;
    title?: string;
    index: number;
    count: number;
    onMove: (from: number, to: number) => void;
    handle?: SortableHandle;
    /** Receives the grip's node, for a caller that puts focus back on it after the entry moved. */
    onNode?: (node: HTMLSpanElement | null) => void;
    className?: string;
}

/** The six-dot handle: drag it, or focus it and use the arrow keys. */
export function SortableGrip({
    label,
    title,
    index,
    count,
    onMove,
    handle,
    onNode,
    className,
}: SortableGripProps) {
    function onKeyDown(event: KeyboardEvent<HTMLSpanElement>) {
        const move = stepMove(event.key, index, count);
        if (!move) return;
        event.preventDefault();
        onMove(move.from, move.to);
    }
    return (
        <span
            ref={(node) => {
                handle?.attach(node);
                onNode?.(node);
            }}
            role="button"
            tabIndex={0}
            title={title}
            aria-label={label}
            data-slot="sortable-grip"
            className={cn(
                'text-muted-foreground focus-visible:outline-primary inline-flex shrink-0 cursor-grab touch-none items-center rounded-xs focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-solid active:cursor-grabbing pointer-coarse:size-11 pointer-coarse:justify-center',
                className,
            )}
            {...handle?.listeners}
            onKeyDown={onKeyDown}
        >
            <GripVertical className="size-3.5" aria-hidden />
        </span>
    );
}
